/*
  Calendar Meeting ID, picked rather than pasted.

  This used to be a bare text box, so linking a meeting space to a calendar
  meant going to a Google or Microsoft console, finding the resource calendar
  and copying an opaque id across by hand.

  A credential now carries the calendars found on the account - the calendar
  forms store them under customProperties.calendarEntries when the sign-in
  returns them - so once a credential is chosen this becomes a list of room
  names, and the id it holds is filled in for you.

  It falls back to the old text box, on purpose, in the three cases where there
  is no list to offer: Exchange credentials, credentials saved before this
  existed, and an existing meeting space whose stored id is not on the list any
  more (usually a room renamed or removed on the provider side - blanking it
  silently would lose the link).
*/

import React, { useState } from "react";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { calendarEntryOptions } from "../utils/calendarProvisioning";

/**
 * @param {object}   credential  the selected Calendar row, or undefined
 * @param {string}   value       the calendarMeetingId currently held
 * @param {function} onChange    called with the new calendarMeetingId
 * @param {number}   width       to match the surrounding fields
 * @param {boolean}  disabled
 * @param {Set}      takenIds    calendar ids already linked to other rooms
 */
const CalendarMeetingIdField = ({
  credential,
  value,
  onChange,
  width = 270,
  disabled = false,
  takenIds,
}) => {
  const [manual, setManual] = useState(false);

  const options = calendarEntryOptions(credential).filter(
    (option) => option.value === value || !takenIds?.has(option.value)
  );
  const valueIsOnTheList =
    !value || options.some((option) => option.value === value);
  const showList = options.length > 0 && !manual && valueIsOnTheList;

  if (showList) {
    return (
      <Box sx={{ width }}>
        <TextField
          required
          select
          fullWidth
          size="small"
          id="calendarMeetingId"
          name="calendarMeetingId"
          label="Calendar meeting room"
          variant="outlined"
          value={value || ""}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        {value ? (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 0.5,
              fontFamily: "monospace",
              fontSize: "0.68rem",
              color: "text.secondary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={value}
          >
            {value}
          </Typography>
        ) : null}
        <Link
          component="button"
          type="button"
          variant="caption"
          underline="hover"
          onClick={() => setManual(true)}
          sx={{ display: "block", mt: 0.25 }}
        >
          Enter an ID manually
        </Link>
      </Box>
    );
  }

  return (
    <Box sx={{ width }}>
      <TextField
        required
        fullWidth
        size="small"
        id="calendarMeetingId"
        name="calendarMeetingId"
        label="Calendar meeting ID"
        variant="outlined"
        value={value || ""}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
      {options.length > 0 ? (
        <Link
          component="button"
          type="button"
          variant="caption"
          underline="hover"
          onClick={() => {
            setManual(false);
            // Cleared so the list opens on a clean choice rather than showing
            // an id that is not one of its options.
            if (!valueIsOnTheList) onChange("");
          }}
          sx={{ display: "block", mt: 0.25 }}
        >
          Pick from the calendar instead
        </Link>
      ) : null}
    </Box>
  );
};

export default CalendarMeetingIdField;
